import { useEffect } from 'react'

import { isAfter, isBefore, parseISO } from 'date-fns'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Input,
  LoadingButton,
} from '@fortem-monorepo/react-ui'

import { useCreateEventMutation } from '@/pages/instruments/market-summary/market-summary.mutations'

import { createEventSchema } from './create-event.schema'

export interface CreateEventDialog extends DialogProps {
  onClose: () => void
}

export const CreateEventDialog: React.FC<CreateEventDialog> = ({ onClose, ...props }) => {
  const form = useFormWithSchema({ mode: 'all' }, createEventSchema)

  const createEventMutation = useCreateEventMutation()

  useEffect(() => {
    form.reset()
    form.trigger()
  }, [props.open])

  const onSubmit = form.handleSubmit(async (_data) => {
    await createEventMutation.mutateAsync(_data)
    onClose()
  })

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open && !form.formState.isSubmitting) {
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Event</DialogTitle>
          <DialogDescription>
            This will create an event, and will become available when adding metrics to a display.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={() => {
                return (
                  <FormItem>
                    <div className="flex items-center gap-1">
                      <FormLabel>Name</FormLabel>
                      <FormError />
                    </div>
                    <Input inputSize={'sm'} placeholder={'Enter a name...'} {...form.register('name')} />
                  </FormItem>
                )
              }}
            />
            <div className={'flex items-center gap-2'}>
              <FormField
                control={form.control}
                name="st_dt"
                render={({ field: { value } }) => {
                  return (
                    <FormItem className={'flex-1'}>
                      <div className="flex items-center gap-1">
                        <FormLabel>Start Date</FormLabel>
                        <FormError />
                      </div>
                      <DatePicker
                        size={'sm'}
                        disabled={(date) => {
                          const endDate = form.getValues('ed_dt')
                          return endDate ? isAfter(date, parseISO(endDate)) : false
                        }}
                        placeholder={'Select a start date...'}
                        selected={value ? parseISO(value) : undefined}
                        onSelect={(date) =>
                          date ? form.setValue('st_dt', toISO8601(date), { shouldValidate: true }) : undefined
                        }
                      />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="ed_dt"
                render={({ field: { value } }) => {
                  return (
                    <FormItem className={'flex-1'}>
                      <div className="flex items-center gap-1">
                        <FormLabel>End Date (optional)</FormLabel>
                        <FormError />
                      </div>
                      <DatePicker
                        size={'sm'}
                        placeholder={'Present (no end date)'}
                        disabled={(date) => {
                          const startDate = form.getValues('st_dt')
                          return startDate ? isBefore(date, parseISO(startDate)) : false
                        }}
                        selected={value ? parseISO(value) : undefined}
                        onSelect={(date) =>
                          form.setValue('ed_dt', date ? toISO8601(date) : null, { shouldValidate: true })
                        }
                      />
                      {value && (
                        <Button
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={() => form.setValue('ed_dt', null, { shouldValidate: true })}
                        >
                          Clear end date
                        </Button>
                      )}
                    </FormItem>
                  )
                }}
              />
            </div>
          </div>
        </Form>
        <DialogFooter>
          <div className="flex items-center gap-2">
            <Button size="sm" type="button" variant={'destructive'} onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              size="sm"
              variant={'default'}
              onClick={onSubmit}
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              Create
            </LoadingButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
